/**
 * CurrencyConverter - Real-time currency converter calculator section
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Calculator, 
  Clock,
  ArrowRight,
  ArrowRightLeft
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CurrencyConverter = ({ currencies }) => {
  const { t, i18n } = useTranslation();
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [amount, setAmount] = useState('1000');
  const [convertedAmount, setConvertedAmount] = useState(0);
  const [amsterdamTime, setAmsterdamTime] = useState({ time: '', date: '' });

  // Amsterdam Time Clock
  useEffect(() => {
    const updateAmsterdamTime = () => {
      const now = new Date();
      const options = { timeZone: 'Europe/Amsterdam' };
      
      const timeStr = now.toLocaleTimeString('en-GB', {
        ...options,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      const dateStr = now.toLocaleDateString(i18n.language, {
        ...options,
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
      
      setAmsterdamTime({ time: timeStr, date: dateStr });
    };

    updateAmsterdamTime();
    const interval = setInterval(updateAmsterdamTime, 1000);
    return () => clearInterval(interval);
  }, [i18n.language]);

  useEffect(() => {
    if (currencies.length > 0 && amount) {
      const fromRate = currencies.find(c => c.code === fromCurrency)?.rate || 1;
      const toRate = currencies.find(c => c.code === toCurrency)?.rate || 1;
      const amountNum = parseFloat(amount) || 0;
      const usdAmount = amountNum / fromRate;
      const result = usdAmount * toRate;
      setConvertedAmount(result);
    }
  }, [amount, fromCurrency, toCurrency, currencies]);

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  const getSymbol = (code) => currencies.find(c => c.code === code)?.symbol || code;

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Converter */}
          <div>
            <div className="text-center lg:text-left mb-12">
              {/* Amsterdam Time Clock */}
              <div className="mb-6 inline-flex flex-col items-center lg:items-start">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <Clock className="w-3 h-3" />
                  <span>{t('home.converter.amsterdamTime')}</span>
                </div>
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-800 dark:to-slate-700 rounded-xl px-5 py-3 shadow-lg border border-slate-700">
                  <div className="font-mono text-3xl md:text-4xl font-bold text-emerald-400 tracking-wider">
                    {amsterdamTime.time}
                  </div>
                  <div className="text-xs text-slate-400 text-center mt-1">
                    {amsterdamTime.date}
                  </div>
                </div>
              </div>
              
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-sm font-medium mb-4">
                <Calculator className="w-4 h-4" />
                {t('home.converter.liveRates')}
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-4">
                {t('home.converter.title')}
              </h2>
              <p className="text-muted-foreground max-w-2xl">
                {t('home.converter.desc')}
              </p>
            </div>
            
            <div className="bg-card rounded-3xl border border-border shadow-lg p-8">
              <div className="space-y-6">
                {/* From Currency */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-muted-foreground">{t('home.converter.youSend')}</label>
                  <div className="flex gap-2">
                    <Select value={fromCurrency} onValueChange={setFromCurrency}>
                      <SelectTrigger className="w-[120px] h-14 text-lg font-medium">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            <span className="flex items-center gap-2">
                              <span className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded">{c.symbol}</span>
                              {c.code}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="flex-1 h-14 text-2xl font-heading font-semibold text-right"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Swap Button */}
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full w-12 h-12 border-2 hover:bg-emerald-500/10 hover:border-emerald-500"
                    onClick={swapCurrencies}
                  >
                    <ArrowRightLeft className="w-5 h-5" />
                  </Button>
                </div>

                {/* To Currency */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-muted-foreground">{t('home.converter.theyReceive')}</label>
                  <div className="flex gap-2">
                    <Select value={toCurrency} onValueChange={setToCurrency}>
                      <SelectTrigger className="w-[120px] h-14 text-lg font-medium">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            <span className="flex items-center gap-2">
                              <span className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded">{c.symbol}</span>
                              {c.code}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex-1 h-14 px-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-end">
                      <span className="text-2xl font-heading font-semibold text-emerald-600 dark:text-emerald-400">
                        {getSymbol(toCurrency)}{convertedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exchange Rate Info */}
              <div className="mt-8 pt-6 border-t border-border">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">{t('home.converter.rate')}</p>
                    <p className="font-heading font-semibold">
                      1 {fromCurrency} = {(convertedAmount / (parseFloat(amount) || 1)).toFixed(4)} {toCurrency}
                    </p>
                  </div>
                  <div className="space-y-1 text-right">
                    <p className="text-sm text-muted-foreground">{t('home.converter.fee')}</p>
                    <p className="font-heading font-semibold text-emerald-600 dark:text-emerald-400">
                      {t('home.converter.feeValue')}
                    </p>
                  </div>
                </div>
                <Link to="/register" className="block mt-6">
                  <Button size="lg" className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
                    {t('home.converter.cta')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Right side - Image with slide-in animation */}
          <div 
            className="relative hidden lg:block animate-slide-in-right"
            style={{
              animation: 'slideInRight 0.8s ease-out forwards'
            }}
          >
            <style>{`
              @keyframes slideInRight {
                from {
                  opacity: 0;
                  transform: translateX(100px);
                }
                to {
                  opacity: 1;
                  transform: translateX(0);
                }
              }
            `}</style>
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-blue-500/20 rounded-3xl blur-3xl" />
            <img
              src="https://static.prod-images.emergentagent.com/jobs/e05dc050-5308-4bfd-9eec-aa169f352982/images/02bcadd8d976d7e3e6f5db028755d43f2a1c8d622bada48d439699802277591d.jpeg"
              alt="ParachPay multi-currency wallet on a phone"
              className="relative rounded-3xl shadow-2xl w-full h-auto"
              data-testid="converter-wallet-image"
            />
          </div>
        </div>

        {/* Popular Currencies */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {['USD', 'EUR', 'GBP', 'JPY'].map((code) => {
            const currency = currencies.find(c => c.code === code);
            return currency ? (
              <div 
                key={code}
                className="p-4 rounded-xl bg-card border border-border text-center hover:shadow-md transition-shadow cursor-pointer"
              >
                <p className="text-2xl font-heading font-bold">{currency.symbol}</p>
                <p className="text-sm font-medium">{code}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  1 USD = {currency.rate.toFixed(2)} {code}
                </p>
              </div>
            ) : null;
          })}
        </div>
      </div>
    </section>
  );
};

export default CurrencyConverter;
